/**
 * CyKL Context - Global State for CyKL Chat System
 * 
 * PROPRIETARY NOTICE: THE ONESCENT DIAL
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Provides global state management for CyKL chat, allowing any component
 * (CyKLButton, OnboardingTutorial, etc.) to open the chat interface.
 * 
 * USAGE:
 * import { useCyKL } from '../contexts/CyKLContext';
 * const { openChat, closeChat, isOpen, sendMessage } = useCyKL();
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const CyKLContext = createContext(null);

export const useCyKL = () => {
  const context = useContext(CyKLContext);
  if (!context) {
    throw new Error('useCyKL must be used within a CyKLProvider');
  }
  return context;
};

export const CyKLProvider = ({ children }) => {
  // Chat visibility state
  const [isOpen, setIsOpen] = useState(false);
  
  // Pending message to send when chat opens
  const [pendingMessage, setPendingMessage] = useState(null);
  
  // Context for the chat (what panel they're viewing, etc.)
  const [chatContext, setChatContext] = useState({});
  
  // Ref for callback when message is sent
  const onMessageSentRef = useRef(null);
  
  /**
   * Open the CyKL chat interface
   * @param {object} options - Optional configuration
   * @param {string} options.initialMessage - Message to send immediately when opened
   * @param {object} options.context - Context about where the user is in the app
   * @param {function} options.onMessageSent - Callback after message is sent
   */
  const openChat = useCallback((options = {}) => {
    const { initialMessage, context, onMessageSent } = options;
    
    if (context) {
      setChatContext(prev => ({ ...prev, ...context }));
    }
    
    if (initialMessage) {
      setPendingMessage(initialMessage);
    }
    
    if (onMessageSent) {
      onMessageSentRef.current = onMessageSent;
    }
    
    setIsOpen(true);
  }, []);
  
  /**
   * Close the CyKL chat interface
   */
  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);
  
  /**
   * Toggle the chat open/closed
   */
  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
  
  /**
   * Update context without opening/closing
   * @param {object} newContext - Context to merge
   */
  const updateContext = useCallback((newContext) => {
    setChatContext(prev => ({ ...prev, ...newContext }));
  }, []);
  
  /**
   * Consume pending message (called by CyKLChat when it processes the message)
   * @returns {string|null} The pending message, or null
   */
  const consumePendingMessage = useCallback(() => {
    const message = pendingMessage;
    setPendingMessage(null);
    
    // Call the callback if set
    if (onMessageSentRef.current) {
      setTimeout(() => {
        onMessageSentRef.current?.();
        onMessageSentRef.current = null;
      }, 0);
    }
    
    return message;
  }, [pendingMessage]);
  
  /**
   * Quick helper to open chat with a specific question
   * @param {string} question - Question to ask CyKL
   */
  const askCyKL = useCallback((question) => {
    openChat({ initialMessage: question });
  }, [openChat]);
  
  const value = {
    // State
    isOpen,
    chatContext,
    hasPendingMessage: !!pendingMessage,
    
    // Actions
    openChat,
    closeChat,
    toggleChat,
    updateContext,
    consumePendingMessage,
    askCyKL
  };
  
  return (
    <CyKLContext.Provider value={value}>
      {children}
    </CyKLContext.Provider>
  );
};

export default CyKLContext;
